/**
 * 選択範囲の切り出しクリップを WAV 化するユーティリティ。
 *
 * WAV 原本からの切り出しは wavSource がバイトコピーで行うため、ここが担うのは
 * decode 経路（mp3 / m4a / FLAC 等）のフォールバック書き出しと、
 * ドラッグ書き出し用の data URL 化。
 *
 * data URL を使うのは実測に基づく。Chrome の DownloadURL は blob: URL を渡すと
 * 必ず 0 バイトのファイルになり、data: URL のみ正しく書き出される
 * （17.28MB / 2304 万文字まで動作確認済み）。
 */

import { buildWavHeader } from './wavSource';

/**
 * ドラッグ書き出しの上限。
 *
 * data URL は base64 かつ UTF-16 文字列なので、メモリ上は Blob の約 2.7 倍
 * （10MB → 約 27MB）を消費する。これを超える選択はドラッグを無効化し、
 * ダウンロードボタンへ誘導する。24bit / 48kHz ステレオで約 35 秒相当。
 */
export const DRAG_EXPORT_MAX_BYTES = 10 * 1024 * 1024;

/** WAV の PCM ビット深度 */
export type WavBitDepth = 16 | 24 | 32;

/**
 * Float32 のチャンネル配列をインターリーブして WAV(RIFF) Blob にする。
 * bitDepth 32 は IEEE float（format 3）、それ以外は整数 PCM（format 1）。
 */
export function encodeWavFromChannels(
  channels: Float32Array[],
  sampleRate: number,
  bitDepth: WavBitDepth,
): Blob {
  const channelCount = channels.length;
  const frameCount = channelCount > 0 ? channels[0].length : 0;
  const bytesPerSample = bitDepth / 8;
  const isFloat = bitDepth === 32;
  const dataSize = frameCount * channelCount * bytesPerSample;

  const body = new ArrayBuffer(dataSize);
  const view = new DataView(body);
  let offset = 0;
  for (let i = 0; i < frameCount; i++) {
    for (let c = 0; c < channelCount; c++) {
      const raw = channels[c][i];
      const v = raw > 1 ? 1 : raw < -1 ? -1 : raw;
      if (isFloat) {
        view.setFloat32(offset, v, true);
      } else if (bitDepth === 16) {
        view.setInt16(offset, Math.round(v * 32767), true);
      } else {
        const n = Math.round(v * 8388607);
        view.setUint8(offset, n & 0xff);
        view.setUint8(offset + 1, (n >> 8) & 0xff);
        view.setUint8(offset + 2, (n >> 16) & 0xff);
      }
      offset += bytesPerSample;
    }
  }

  const header = buildWavHeader(
    {
      sampleRate,
      channels: channelCount,
      bitsPerSample: bitDepth,
      encoding: isFloat ? 'float' : 'int',
    },
    dataSize,
  );
  return new Blob([header, body], { type: 'audio/wav' });
}

/** AudioBuffer の範囲を切り出して WAV Blob にする（decode 経路のフォールバック用） */
export function sliceAudioBufferToWav(
  buffer: AudioBuffer,
  startSeconds: number,
  endSeconds: number,
  bitDepth: WavBitDepth,
): Blob {
  const from = Math.max(0, Math.floor(startSeconds * buffer.sampleRate));
  const to = Math.min(buffer.length, Math.ceil(endSeconds * buffer.sampleRate));
  const channels: Float32Array[] = [];
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    channels.push(buffer.getChannelData(c).subarray(from, Math.max(from, to)));
  }
  return encodeWavFromChannels(channels, buffer.sampleRate, bitDepth);
}

/** Blob を data URL に変換する（DownloadURL に渡せる唯一の形式） */
export function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error('data URL 変換に失敗しました'));
    reader.readAsDataURL(blob);
  });
}

/** 元ファイル名と選択範囲から書き出しファイル名を組み立てる */
export function buildClipFileName(
  sourceName: string,
  startSeconds: number,
  endSeconds: number,
): string {
  const base = sourceName.replace(/\.[^.]+$/, '') || 'clip';
  const stamp = (seconds: number) => seconds.toFixed(2).replace('.', '_');
  return `${base}_${stamp(startSeconds)}-${stamp(endSeconds)}.wav`;
}
